// Waypoint List - Reusable component with drag-and-drop functionality
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface WaypointListItem {
  id: string;
  name?: string;
  destination?: string;
  /** @deprecated use name */
  content_name?: string;
  /** @deprecated use destination */
  waypoint_name?: string;
  version: number;
  floor: number;
  is_halfway: boolean;
  is_return_point: boolean;
}

interface WaypointListProps {
  waypoints: WaypointListItem[];
  onReorder: (waypoints: WaypointListItem[]) => void;
  onRemove: (index: number) => void;
  maxHeight?: string;
}

// Sortable Waypoint Item Component
interface SortableWaypointItemProps {
  waypoint: WaypointListItem;
  index: number;
  onRemove: (index: number) => void;
}

const SortableWaypointItem: React.FC<SortableWaypointItemProps> = ({ waypoint, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: waypoint.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        mb: 1,
        bgcolor: 'background.paper',
      }}
    >
      <DragIcon {...attributes} {...listeners} sx={{ mr: 1, cursor: 'grab' }} />
      <ListItemText
        primary={`${index + 1}. ${waypoint.name ?? waypoint.content_name ?? ''}`}
        secondary={`Destination: ${waypoint.destination ?? waypoint.waypoint_name ?? ''} | v${waypoint.version} | Floor ${waypoint.floor}${waypoint.is_halfway ? ' · halfway' : ''}${waypoint.is_return_point ? ' · return' : ''}`}
      />
      <ListItemSecondaryAction>
        <Tooltip title="Remove waypoint">
          <IconButton edge="end" onClick={() => onRemove(index)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const WaypointList: React.FC<WaypointListProps> = ({ waypoints, onReorder, onRemove, maxHeight = '900px' }) => {
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = waypoints.findIndex(item => item.id === active.id);
      const newIndex = waypoints.findIndex(item => item.id === over.id);
      const newWaypoints = arrayMove(waypoints, oldIndex, newIndex);
      onReorder(newWaypoints);
    }
  };

  if (waypoints.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">
          No waypoints added yet. Click "Add Waypoint" to get started.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ maxHeight, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={waypoints.map(wp => wp.id)} strategy={verticalListSortingStrategy}>
          <List>
            {waypoints.map((waypoint, index) => (
              <SortableWaypointItem key={waypoint.id} waypoint={waypoint} index={index} onRemove={onRemove} />
            ))}
          </List>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default WaypointList;
